import React, { useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';

const QRCodeGenerator = ({ text, size = 200, level = 'M' }) => {
  const canvasRef = useRef(null);
  const qrCodeUrl = `https://justpasteit.in/pastetext`;

  useEffect(() => {
    if (text && canvasRef.current) {
      try {
        // Generate QR code and render to canvas
        QRCode.toCanvas(canvasRef.current, qrCodeUrl, {
          width: size,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          },
          errorCorrectionLevel: level
        }, (error) => {
          if (error) {
            console.error('Error generating QR code:', error);
          }
        });
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
  }, [text, size, level, qrCodeUrl]);

  const handleDownload = () => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const link = document.createElement('a');
      link.download = `qr-code-${text}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Just Paste It - Shared Text',
          text: `Access your shared text with code: ${text}`,
          url: qrCodeUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
        // Fallback to copy to clipboard
        handleCopyToClipboard();
      }
    } else {
      // Fallback to copy to clipboard
      handleCopyToClipboard();
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(qrCodeUrl).then(() => {
      // You could add a toast notification here
      console.log('URL copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy URL: ', err);
    });
  };

  if (!text) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        mt: 2
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ mb: 1, fontSize: '1rem', fontWeight: 600 }}
      >
        Scan QR Code to Open on Mobile
      </Typography>
      
      <Box sx={{ position: 'relative', mb: 2 }}>
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Tooltip title="Download QR Code">
          <IconButton 
            onClick={handleDownload}
            size="small"
            sx={{ 
              backgroundColor: '#1976d2', 
              color: 'white',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Share URL">
          <IconButton 
            onClick={handleShare}
            size="small"
            sx={{ 
              backgroundColor: '#4caf50', 
              color: 'white',
              '&:hover': { backgroundColor: '#388e3c' }
            }}
          >
            <ShareIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography 
        variant="caption" 
        sx={{ 
          fontSize: '0.75rem', 
          color: '#666', 
          textAlign: 'center',
          wordBreak: 'break-all'
        }}
      >
        URL: {qrCodeUrl}
      </Typography>
    </Box>
  );
};

export default QRCodeGenerator;
